import makeStyles from '@mui/styles/makeStyles';
import React, {useEffect, useRef, useState} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {FormLabel, Paper, Popover} from '@mui/material';
import {useTranslation} from 'react-i18next';
import config from '../../../app/config';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import {formNameException} from '../../../components/notes/notesUtil';

const vis = require('vis-timeline/standalone');

const useStyles = makeStyles((theme) => ({
    root: {
        margin: '10px 0',
        padding: 5,
    },
    item: {
        padding: 10,
    },
    title: {
        marginBottom: 10,
    },
    text: {
        marginLeft: 10,
        fontWeight: 'bold',
        fontStyle: 'italic',
    },
    green: {
        backgroundColor: theme.palette.acuityColors.color4.background,
        borderColor: theme.palette.acuityColors.color4.background,
        color: theme.palette.acuityColors.color4.text,
    },
    red: {
        backgroundColor: theme.palette.acuityColors.color1.background,
        borderColor: theme.palette.acuityColors.color1.background,
        color: theme.palette.acuityColors.color1.text,
    },
    normal: {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        color: '#fff',
    },
    timelineContainer: {
        '& .vis-timeline': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 6,
            fontFamily: 'Inter',
            fontSize: 12,
            color: '#fff',
        },
        '& .vis-time-axis .vis-text.vis-major': {
            color: theme.palette.mode === 'light' ? '#373737' : '#d2d5d9',
            fontWeight: 'bold',
        },
        '& .vis-time-axis .vis-text.vis-minor': {
            color: theme.palette.mode === 'light' ? '#373737' : '#d2d5d9',
        },
        '& .vis-time-axis .vis-grid.vis-major': {
            borderColor: theme.palette.mode === 'light' ? '#373737' : '#d2d5d9',
            borderWidth: 2,
        },
        '& .vis-time-axis .vis-grid.vis-minor': {
            borderColor: theme.palette.mode === 'light' ? '#373737' : '#d2d5d9',
        },
    },
}));

const TimelineComponent = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const windowDimensions = useWindowDimensions();

    const timelineContainer = useRef(null);
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [item, setItem] = useState(null);

    const handleItemClick = (event) => {
        setPopoverAnchor({x: event.pageX, y: event.pageY});
    };

    const handleClosePopover = () => {
        setPopoverAnchor(null);
        setItem(null);
    };

    useEffect(() => {
        const data = [
            {
                id: 'start',
                start: moment(props.startTime),
                content: 'Admission',
                className: classes.green,
                selectable: false,
            },
            {
                id: '4hBreach',
                start: moment(props.startTime).add(4, 'hours'),
                content: '4h Breach',
                className: classes.red,
                selectable: false,
            },
        ];

        if (props.discharged) {
            data.push({
                id: 'discharged',
                start: moment(props.endTime),
                content: 'Discharged',
                className: classes.green,
                selectable: false,
            });
        }

        _.forEach(props.events, e => {
            data.push({
                id: e.unique_id,
                content: getNoteName(e.type),
                start: e.date,
                selectable: false,
                className: classes.normal,
            });
        });

        const items = new vis.DataSet(data);

        const options = {
            start: props.startTime,
            end: props.endTime,
            min: props.startTime.subtract(1, 'days'),
            max: props.endTime.add(1, 'days'),
            locale: 'en_EN',
            height: windowDimensions?.height - 350,
        };

        const tmLine = new vis.Timeline(timelineContainer.current, items, options);
        tmLine.on('click', function (properties) {
            const {item} = properties;
            const find = _.find(props.events, {unique_id: item});
            if (find) {
                handleItemClick(properties?.event);
                setItem(find);
            }
        });

        return () => {
            tmLine.destroy();
        };
    }, [props.events, props.startTime, props.discharged, windowDimensions?.height]);

    const getNoteName = (noteType) => {
        if (noteType) {
            let text = noteType;
            if (noteType.includes('.')) {
                const arr = noteType.split('.');
                text = _.last(arr);
            }

            const exception = formNameException.get(text);
            if (exception) text = exception;

            return _.startCase(text);
        }
        return '-';
    };

    const renderList = (items) => {
        if (_.isEmpty(items)) return null;

        return (
            <>
                {items.map((i, index) => {
                    const name = Object.keys(i)[0];
                    const clinician = _.isString(i[name]?.clinician) ? i[name]?.clinician : i[name]?.clinician?.__description;

                    return (
                        <div key={index} className={classes.title}>
                            <FormLabel>
                                {t('Action Type')}: {getNoteName(name)}
                            </FormLabel>

                            <div className={classes.text}>
                                <div>{clinician || t('Not set')}</div>
                                <div>{i[name]?.date ? moment(i[name]?.date).format(config.defaultDateTimeFormat) : ''}</div>
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    const renderItem = () => {
        if (!item) return null;

        return (
            <div>
                <div className={classes.title}>
                    <FormLabel>
                        {t('Action Type')}:
                    </FormLabel>

                    <span className={classes.text}>
                        {getNoteName(item?.type)}
                    </span>
                </div>

                <div className={classes.title}>
                    <FormLabel>
                        {t('Action Date')}:
                    </FormLabel>

                    <span className={classes.text}>
                        {moment(item?.date).format(config.defaultDateTimeFormat + ':ss')}
                    </span>
                </div>

                <div className={classes.title}>
                    <FormLabel>
                        {t('Action By')}:
                    </FormLabel>

                    <span className={classes.text}>
                        {item?.user_fullname || item?.user}
                    </span>
                </div>

                {item?.clinician_set &&
                    <div className={classes.title}>
                        <FormLabel>
                            {t('Clinician Set')}:
                        </FormLabel>

                        <span className={classes.text}>
                            {_.isString(item?.clinician_set) ? item?.clinician_set : item?.clinician_set?.__description}
                        </span>
                    </div>
                }

                {item?.date_set &&
                    <div className={classes.title}>
                        <FormLabel>
                            {t('Date Form Set')}:
                        </FormLabel>

                        <span className={classes.text}>
                            {moment(item?.date_set).format(config.defaultDateTimeFormat)}
                        </span>
                    </div>
                }

                {item?.clinicians_list && renderList(item.clinicians_list)}
            </div>
        );
    };

    return (
        <div className={classes.root}>
            <div ref={timelineContainer} className={classes.timelineContainer}/>

            <Popover
                open={Boolean(popoverAnchor)}
                anchorReference="anchorPosition"
                anchorPosition={
                    popoverAnchor
                        ? {top: popoverAnchor.y, left: popoverAnchor.x}
                        : undefined
                }
                onClose={handleClosePopover}
            >
                <Paper className={classes.item}>
                    {renderItem()}
                </Paper>
            </Popover>
        </div>
    );
};

export default TimelineComponent;

TimelineComponent.propTypes = {
    startTime: PropTypes.instanceOf(moment),
    endTime: PropTypes.instanceOf(moment),
    events: PropTypes.array,
    discharged: PropTypes.bool,
};

TimelineComponent.defaultProps = {
    events: [],
    discharged: false,
};